import type { RequestOptions } from '@cravo/core'
import { api } from '../api-client'

export interface PlatformDeliveryConfig {
  /** Platform-wide cap on delivery radius (km). Drives the registration slider. */
  maxDeliveryRadiusKm: number
  // Backend may add more fields over time (min/max delivery fee, default tip, etc.)
}

export interface PlatformVersionConfig {
  /** Minimum supported web build version (e.g. "1.0.5"). Below this, force update. */
  minWebVersion?: string
  /** Latest available web version (recommended update). */
  latestWebVersion?: string
  /** When true, app should show the maintenance screen and block usage. */
  maintenanceMode?: boolean
  /** Optional message shown on maintenance screen. */
  maintenanceMessage?: string
  /** Optional URL to send users to for an update. */
  updateUrl?: string
}

export interface PlatformCurrencyConfig {
  /** ISO 4217 currency code (e.g. 'USD', 'PKR'). */
  code: string
  /** Symbol e.g. '$', 'Rs'. Optional — derive from code if missing. */
  symbol?: string
  /** Locale used for `Intl.NumberFormat`, e.g. 'en-US'. */
  locale?: string
}

export const platformConfigApi = {
  /**
   * GET /platform-config/delivery
   * Mobile parity: registration_repository.dart `fetchMaxDeliveryRadius`.
   * Falls back to 15 km client-side if the field is missing.
   */
  async getDelivery(options?: RequestOptions): Promise<PlatformDeliveryConfig> {
    const data = await api.get<Partial<PlatformDeliveryConfig> & Record<string, unknown>>(
      '/platform-config/delivery',
      options,
    )
    return {
      maxDeliveryRadiusKm:
        typeof data?.maxDeliveryRadiusKm === 'number' && data.maxDeliveryRadiusKm > 0
          ? data.maxDeliveryRadiusKm
          : 15,
    }
  },

  /**
   * GET /platform-config/version
   * Mobile parity: business_splash_controller.dart maintenance/force-update gate.
   * Returns nothing meaningful on backends that don't expose this — caller treats absence as "up to date".
   */
  async getVersion(options?: RequestOptions): Promise<PlatformVersionConfig> {
    try {
      const data = await api.get<Partial<PlatformVersionConfig> & Record<string, unknown>>(
        '/platform-config/version',
        options,
      )
      return {
        minWebVersion: typeof data?.minWebVersion === 'string' ? data.minWebVersion : undefined,
        latestWebVersion:
          typeof data?.latestWebVersion === 'string' ? data.latestWebVersion : undefined,
        maintenanceMode: Boolean(data?.maintenanceMode),
        maintenanceMessage:
          typeof data?.maintenanceMessage === 'string' ? data.maintenanceMessage : undefined,
        updateUrl: typeof data?.updateUrl === 'string' ? data.updateUrl : undefined,
      }
    } catch {
      return {}
    }
  },

  /**
   * GET /platform-config/currency
   * Mobile parity: currency_service.dart fetches platform-wide currency config.
   * Falls back to USD if the backend doesn't expose it.
   */
  async getCurrency(options?: RequestOptions): Promise<PlatformCurrencyConfig> {
    try {
      const data = await api.get<Partial<PlatformCurrencyConfig> & Record<string, unknown>>(
        '/platform-config/currency',
        options,
      )
      return {
        code: typeof data?.code === 'string' && data.code ? data.code : 'USD',
        symbol: typeof data?.symbol === 'string' ? data.symbol : undefined,
        locale: typeof data?.locale === 'string' ? data.locale : undefined,
      }
    } catch {
      return { code: 'USD' }
    }
  },
}
